import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { UserService } from 'src/app/core/api/user/user.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { MultiSelectComponent } from 'src/app/shared/components/atoms/multi-select/multi-select.component';
import { DialogModalSelectData } from 'src/app/shared/components/molecules/dialog-modal-select/dialog-modal-select.component';
import {
  DialogModalData,
  SecondaryContent,
} from 'src/app/shared/components/molecules/dialog-modal/dialog-modal.component';
import { Region } from 'src/app/shared/models/dtos/region/region';
import {
  AdminEvent,
  AdministratorDTO,
} from 'src/app/shared/models/dtos/responses/administrator-dto/administrator-dto';
import { maxStringLength } from 'src/app/utils/checkMaxLength';
import { selectableRegions } from 'src/app/utils/regions';
import { Roles, selectableRoles } from 'src/app/utils/roles';
import { SelectorData } from 'src/app/utils/selectorDataType';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { RegionalAdminService } from 'src/app/control-panel/services/regional-admins/regional-admins.service';

@Component({
  selector: 'app-admin-list-item',
  templateUrl: './admin-list-item.component.html',
  styleUrls: ['./admin-list-item.component.scss'],
})

/**
 * admin list item
 */
export class AdminListItemComponent implements OnInit {
  @Input() admin!: AdministratorDTO;
  @ViewChild('regionMultiSelect') regionMultiSelect!: MultiSelectComponent;
  @ViewChild('roleSelect') roleSelect!: MultiSelectComponent;
  @Output() itemChanged = new EventEmitter<AdminEvent<AdministratorDTO>>();
  alt = 'admin-picture';

  isGlobalAdmin = false;
  isLoggedUser = false;
  allRegions = '';
  loggedUserRole = '';
  availableRoles = selectableRoles;
  availableRegions = selectableRegions;
  mappedRegions: SelectorData[] = [];
  mappedRole: SelectorData = {
    label: '',
    value: undefined,
  };
  entity: string = 'Regions';
  previousRegions: number[] = [];

  readonly labels = {
    promoteGlobalAdmin: 'promote-to-global-admin',
    demoteGlobalAdmin: 'demote-to-global-admin',
    warnGlobalAdminPromotion: 'global-admin-promotion-warning',
    warnGlobalAdminDemotion: 'global-admin-demotion-warning',
    globalAdminAmountLimit: 'global-admin-amount-limit',
    globalAdminInfo: 'global-admin-info',
    regionChange: 'region-change',
    contentRegionCheckboxesEmpty: 'region-checkboxes-empty-1',
    warnAdminRegionChange: 'region-checkboxes-empty-2',
    globalAdminLimit: 'global-admin-limit',
    ok: 'ok',
  };
  readonly deleteAdminLabels = {
    deleteSysadminTitle: 'delete-sysadmin.title',
    confirmBtn: 'delete-sysadmin.confirm-btn',
    cancelBtn: 'delete-sysadmin.cancel-btn',
    successfulMsg: 'delete-sysadmin.successful-msg',
  };
  readonly snackBarLabels = {
    regionSuccess: 'region-change-success',
    roleSuccess: 'role-change-success',
    error: 'error-changes-not-saved',
  };

  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    private translationService: TranslationService,
    private snackBarService: SnackBarService,
    private readonly dialogService: DialogService,
    private regionalAdminService: RegionalAdminService,
    private authenticatedUserService: AuthenticatedUserService
  ) {}

  /**
   * ng onInit
   */
  ngOnInit(): void {
    this.translationService.translateString(this.alt).then((translated) => {
      this.alt = translated;
    });
    this.translationService.translateObject(this.deleteAdminLabels);
    this.mappedRegions = this.mapRegions(this.admin.regions);
    this.previousRegions = this.mappedRegions.map((region) => region.value);
    this.mappedRole = { label: this.admin.role, value: this.admin.role };
    this.isGlobalAdmin = this.admin.role === Roles.GLOBAL_ADMIN;
    this.loggedUserRole = this.getRolePlaceholder(this.admin.role);
    this.isLoggedUser =
      this.admin.id === this.authenticatedUserService.admin?.id;
    this.translationService
      .translateString('all-regions')
      .then((translated) => {
        this.allRegions = translated;
      });
  }

  /**
   * Returns regions formated for selectorData
   * @param {Region[]} regions
   * @return {SelectorData[]}
   */
  mapRegions(regions: Region[]): SelectorData[] {
    return regions.map((region) => ({
      label: region.name,
      value: region.id,
    }));
  }

  /**
   * Returns regions names
   * @param {Region[]} regions
   * @return {string[]}
   */
  mapRegionsNames(regions: Region[]): string[] {
    return regions.map((region) => region.name);
  }

  /**
   * Checks if admin name length is too big
   *
   * @param {string} name
   * @return {boolean}
   */
  public maxNameLength(name: string): boolean {
    return maxStringLength(name, 30);
  }

  /**
   * Gets the view name for a given profile role
   * @param {string} currentRole
   * @return {string}
   */
  getRolePlaceholder(currentRole: string): string {
    const rolePlaceholder = this.availableRoles.find(
      (role) => role.value === currentRole
    )?.label;

    return rolePlaceholder === undefined
      ? 'Select an option below'
      : rolePlaceholder;
  }

  /**
   * @param {MatSelectChange} event
   * @return {void}
   */
  async handleRoleChange(event: MatSelectChange): Promise<void> {
    if (event.value === Roles.GLOBAL_ADMIN) {
      const dialogData: DialogModalData = {
        title: await this.translationService.translateString(
          this.labels.promoteGlobalAdmin
        ),
        secondaryContent: await this.getSecondaryRoleContent(),
        successButtonText: 'Promote',
        failButtonText: 'Cancel',
      };
      this.dialogService.openDialog(
        dialogData,
        () => {
          this.changeRoleRequest(event, []);
        },
        () => {
          this.roleSelect.resetMultiSelectValue(this.mappedRole.value);
          event.source.writeValue(this.mappedRole.value);
        }
      );
    } else if (
      event.value !== Roles.GLOBAL_ADMIN &&
      this.admin.role === Roles.GLOBAL_ADMIN
    ) {
      const dialogData: DialogModalSelectData = {
        admin: this.admin,
        role: event.value,
      };
      this.dialogService.openGlobalAdminDemotion(
        dialogData,
        (selectedRegions) => {
          this.changeRoleRequest(event, selectedRegions);
        },
        () => {
          this.roleSelect.resetMultiSelectValue(this.mappedRole.value);
          event.source.writeValue(this.mappedRole.value);
        }
      );
    } else {
      this.changeRoleRequest(
        event,
        this.admin.regions.map((region) => region.id)
      );
    }
  }

  /**
   * Request to promote an admin
   * @param {MatSelectChange} event
   * @param {number[]} regions
   */
  private changeRoleRequest(event: MatSelectChange, regions: number[]) {
    this.userService
      .updateAdminRole(this.admin.id, event.value, regions)
      .subscribe({
        next: async (response) => {
          this.mappedRole = { label: event.value, value: event.value };
          this.snackBarService.success(
            await this.translationService.translateString(
              this.snackBarLabels.roleSuccess
            )
          );
          this.itemChanged.emit({ action: 'changed', payload: response });
          this.regionalAdminService.voidRegions =
            this.regionalAdminService.getRegionalAdminCount();
        },
        error: async (err) => {
          const errorObject = err.error;
          event.source.writeValue(this.mappedRole.value);
          if (
            errorObject.errorMessage.includes('limit of 2 has been reached')
          ) {
            this.openGlobalAdminLimitModal();
          } else {
            this.snackBarService.error(
              await this.translationService.translateString(
                this.snackBarLabels.error
              )
            );
          }
        },
      });
  }

  /**
   * Opens the modal alerting the limit of global admin was reached
   */
  private async openGlobalAdminLimitModal() {
    const dialogData: DialogModalData = {
      title: await this.translationService.translateString(
        this.labels.promoteGlobalAdmin
      ),
      secondaryContent: [
        {
          content: await this.translationService.translateString(
            this.labels.globalAdminLimit
          ),
          style: {
            color: 'var(--State-colors-Vermelho, #B31E1E)',
            fontFamily: 'Helvetica',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '125%',
          },
        },
      ],
      failButtonText: 'Close',
    };
    this.dialogService.openDialog(dialogData);
  }

  /**
   * Updates region value for admin List
   * @param {MatSelectChange} event
   */
  async handleRegionChange(event: MatSelectChange): Promise<void> {
    if (event.value.length === 0) {
      this.dialogService.openDialog({
        title: await this.translationService.translateString(
          this.labels.regionChange
        ),
        content: await this.translationService.translateString(
          this.labels.contentRegionCheckboxesEmpty
        ),
        secondaryContent: await this.getSecondaryRegionContent(),
        successButtonText: this.labels.ok,
      });
      event.source.writeValue(this.previousRegions);
      this.regionMultiSelect.resetMultiSelectValue(this.previousRegions);
    } else if (
      JSON.stringify(event.value) !== JSON.stringify(this.previousRegions)
    ) {
      this.mappedRegions = this.availableRegions.filter((region) =>
        event.value.includes(region.value)
      );
    }
  }

  /**
   * Saves region changes made on admin list at multiselector closure
   */
  async handleSaveRegionChange(): Promise<void> {
    const updatedRegions = this.mappedRegions.map((region) => region.value);
    if (
      JSON.stringify(updatedRegions) !== JSON.stringify(this.previousRegions)
    ) {
      this.userService
        .updateAdminRegions(this.admin.id, updatedRegions)
        .subscribe({
          next: async () => {
            this.previousRegions = [...updatedRegions];
            this.snackBarService.success(
              await this.translationService.translateString(
                this.snackBarLabels.regionSuccess
              )
            );
            this.regionalAdminService.voidRegions =
              this.regionalAdminService.getRegionalAdminCount();
          },
          error: async () => {
            this.regionMultiSelect.resetMultiSelectValue(this.previousRegions);
            this.mappedRegions = this.availableRegions.filter((region) =>
              this.previousRegions.includes(region.value)
            );
            this.snackBarService.error(
              await this.translationService.translateString(
                this.snackBarLabels.error
              )
            );
          },
        });
    }
  }

  /**
   * @param {MatSelectChange} event
   *
   */
  private async getSecondaryRoleContent(): Promise<SecondaryContent[]> {
    // TODO refactor styles so its less verbose
    return [
      {
        content: await this.translationService.translateString(
          this.labels.warnGlobalAdminPromotion,
          { userName: this.admin?.name }
        ),
        style: {
          color: 'var(--Cinzas-400, #19212B)',
          fontFamily: 'Helvetica',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
        },
      },
      {
        content: await this.translationService.translateString(
          this.labels.globalAdminInfo
        ),
        style: {
          color: 'var(--Cinzas-400, #19212B)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
        },
      },
      {
        content: await this.translationService.translateString(
          this.labels.globalAdminAmountLimit
        ),
        style: {
          color: 'var(--Cinzas-300, #4E4E4E)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '700',
          lineHeight: '125%',
        },
      },
      {
        content: await this.translationService.translateString(
          'You can update these roles at any moment'
        ),
        style: {
          color: 'var(--Cinzas-300, #4E4E4E)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
        },
      },
    ];
  }

  /**
   * @param {MatSelectChange} event
   *
   */
  private async getSecondaryRegionContent(): Promise<SecondaryContent[]> {
    // TODO refactor styles so its less verbose
    return [
      {
        content: await this.translationService.translateString(
          this.labels.warnAdminRegionChange
        ),
        style: {
          color: 'var(--Cinzas-400, #19212B)',
          fontFamily: 'Helvetica',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
        },
      },
    ];
  }

  /**
   * Delete Admin
   * @param {Event} event
   */
  public async deleteAdmin(event: Event) {
    (event.currentTarget as HTMLElement).blur();
    const dialogData: DialogModalData = {
      title: this.deleteAdminLabels.deleteSysadminTitle,
      content: await this.translationService.translateString(
        'delete-sysadmin.description',
        { name: this.admin.name }
      ),
      failButtonText: this.deleteAdminLabels.cancelBtn,
      successButtonText: this.deleteAdminLabels.confirmBtn,
    };
    this.dialogService.openDialog(dialogData, () => {
      this.userService.deleteAdmin(this.admin.id).subscribe({
        next: () => {
          this.snackBarService.success(this.deleteAdminLabels.successfulMsg);
          this.itemChanged.emit({ action: 'deleted', payload: this.admin });
        },
      });
    });
  }
}
